import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Public Stripe payment-success landing page. Stripe redirects the
 * customer here after they complete the Checkout session.
 *
 * What this page does:
 *  1. Pulls the session_id Stripe injected via the {CHECKOUT_SESSION_ID}
 *     template variable in our success URL.
 *  2. Calls `/api/payment/stripe/verify-session` which:
 *      - re-verifies the session payment_status with Stripe (defence
 *        against tampered URLs)
 *      - flips our `payment_links` + parent `order` / `lead` rows to
 *        paid.
 *  3. Shows the customer a clean confirmation.
 *
 * Why this matters: previously after a customer paid via a
 * driver-generated Stripe link, the order's payment_status never
 * updated because there was no webhook handler AND no client-side
 * verify. The customer would see a Stripe success page; the merchant
 * would see an unpaid order in Opswarex. This page closes that loop.
 *
 * The webhook at /api/payment/stripe/webhook is the durability path —
 * it'll mark the row paid even if the customer closes the tab before
 * this page loads. Both are idempotent.
 */
const PaymentSuccessPage = () => {
  const [params] = useSearchParams();
  const [state, setState] = useState({ status: 'verifying', message: '' });

  useEffect(() => {
    const sessionId = params.get('session_id');
    if (!sessionId) {
      setState({ status: 'unknown', message: '' });
      return;
    }
    const token = localStorage.getItem('token');
    // The customer hitting this page may not be logged in (they're
    // the merchant's customer, not an Opswarex user). The endpoint
    // accepts requests with NO auth header by also resolving company
    // context from the session metadata — but to keep it simple we
    // send whatever token we have.
    axios.get(`${API}/payment/stripe/verify-session?session_id=${encodeURIComponent(sessionId)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => {
        if (r.data?.paid) {
          setState({ status: 'paid', message: '' });
        } else {
          setState({ status: 'unpaid', message: `Stripe status: ${r.data?.status || 'unknown'}` });
        }
      })
      .catch((err) => {
        // Even if verify fails, the webhook will reconcile within
        // seconds, so we still show success to the customer.
        setState({ status: 'paid_unverified', message: err?.response?.data?.detail || '' });
      });
  }, [params]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 text-slate-100 px-4">
      <div className="max-w-md w-full text-center bg-slate-800 border border-slate-700 rounded-xl p-8 shadow-xl" data-testid="payment-success-page">
        {state.status === 'verifying' && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-amber-400 animate-spin mb-4" />
            <h1 className="text-2xl font-semibold mb-2">Verifying your payment…</h1>
            <p className="text-sm text-slate-400">One moment please.</p>
          </>
        )}
        {(state.status === 'paid' || state.status === 'paid_unverified') && (
          <>
            <CheckCircle className="w-14 h-14 mx-auto text-emerald-400 mb-4" />
            <h1 className="text-2xl font-semibold mb-2">Payment Successful 🎉</h1>
            <p className="text-sm text-slate-400">
              Thank you! Your payment has been received and the order will be marked as paid in our system.
              You can safely close this window.
            </p>
          </>
        )}
        {state.status === 'unpaid' && (
          <>
            <AlertTriangle className="w-14 h-14 mx-auto text-amber-400 mb-4" />
            <h1 className="text-2xl font-semibold mb-2">Payment Pending</h1>
            <p className="text-sm text-slate-400">
              We couldn't confirm the payment yet. {state.message ? `(${state.message})` : ''}
              If you've already paid, please give us a few minutes — we'll auto-update once Stripe confirms.
            </p>
          </>
        )}
        {state.status === 'unknown' && (
          <>
            <AlertTriangle className="w-14 h-14 mx-auto text-amber-400 mb-4" />
            <h1 className="text-2xl font-semibold mb-2">No Session Found</h1>
            <p className="text-sm text-slate-400">
              We couldn't find a payment session in the URL. If you completed a payment, please contact support.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
